<template>
  <div class="mt-3">
    <!--For CLipart layers-->
    <p class="title is-6 has-text-centered">Number Line <b-button @click="toggle()" type="is-success" size="is-small">Toggle</b-button></p>
    
    <div v-if="number_line.show" class="columns is-multiline">

      <div class="column is-3">
        <b-field label="Canvas Width">
          <b-numberinput v-model="number_line.canvas_width"></b-numberinput>
        </b-field>
      </div>

      <div class="column is-3">
        <b-field label="Canvas Height">
          <b-numberinput v-model="number_line.canvas_height"></b-numberinput>
        </b-field>
      </div>

      <div class="column is-3">
        <b-field label="X-Axis Margin">
          <b-numberinput v-model="number_line.x_margin"></b-numberinput>
        </b-field>
      </div>

      <div class="column is-3">
        <b-field label="Start Number">
          <b-numberinput v-model="number_line.start_num" :step="0.1"></b-numberinput>
        </b-field>
      </div>
 
      <div class="column is-3">
        <b-field label="End Number">
          <b-numberinput v-model="number_line.end_num" :step="0.1"></b-numberinput>
        </b-field>
      </div>

      <div class="column is-3">
        <b-field label="Step">
          <b-numberinput v-model="number_line.step" :step="0.1"></b-numberinput>
        </b-field>
      </div>

      <div class="column is-3">
        <b-field label="Line Stroke Weight">
          <b-numberinput v-model="number_line.stroke_weight"></b-numberinput>
        </b-field>
      </div>

      <div class="column is-3">
        <b-field label="Text Size">
          <b-numberinput v-model="number_line.text_size"></b-numberinput>
        </b-field>
      </div>

      <div class="column is-3">
        <b-field label="Line Color">
          <b-input v-model="number_line.line_color"></b-input>
        </b-field>
      </div>

      <div class="column is-3">
        <b-field label="Text Color">
          <b-input v-model="number_line.text_color"></b-input>
        </b-field>
      </div>

      <div class="column is-3">
        <b-field label="Input Width">
          <b-numberinput v-model="number_line.input_width"></b-numberinput>
        </b-field>
      </div>

      <div class="column is-3">
        <b-field label="Input Height">
          <b-numberinput v-model="number_line.input_height"></b-numberinput>
        </b-field>
      </div>

      <div class="column is-3">
        <b-field label="Input Border Weight">
          <b-numberinput v-model="number_line.input_border_weight"></b-numberinput>
        </b-field>
      </div>

      <div class="column is-3">
        <b-field label="Input Border Color">
          <b-input v-model="number_line.input_border_color"></b-input>
        </b-field>
      </div>

      <div class="column is-3">
        <b-field label="Input Margin (From Center)">
          <b-numberinput v-model="number_line.input_top_margin"></b-numberinput>
        </b-field>
      </div>

      <div class="column is-3">
        <b-field label="Text Margin (From Center)">
          <b-numberinput v-model="number_line.text_top_margin"></b-numberinput>
        </b-field>
      </div>

      <div class="column is-3">
        <b-field label="Make Number Selectable">
          <b-switch v-model="number_line.allow_select"></b-switch>
        </b-field>
      </div>

      <div class="column is-12">
        <p class="title is-6">Inputs <b-button @click="addInputToNumberLine()" type="is-success" size="is-small">Add</b-button></p>
        <div v-for="(input_num, i) in number_line.inputs" :key="`NUMBERLINE_INP_${i}`" class="mx-2" style="width: 100px;float:left">
          <b-field>
            <b-input v-model="number_line.inputs[i]"></b-input>
            <span class="control"><b-button @click="removeInputFromNumberLine(i)" type="is-danger" icon-left="close"></b-button></span>
          </b-field>
        </div>
      </div>

      <!--Arrow settings-->
      <div class="column is-12">
        <p class="title is-5">Dealing with arrows <b-button @click="addArrowToNumberLine()" type="is-success" size="is-small">Add</b-button></p>
        
        <b-table v-if="number_line.arrows && number_line.arrows.length" :data="number_line.arrows">
          <b-table-column v-slot="props" field="from" label="From Number">
            <b-input v-model="props.row.from"></b-input>
          </b-table-column>
          <b-table-column v-slot="props" field="step" label="Step">
            <b-input v-model="props.row.step"></b-input>
          </b-table-column>
          <b-table-column v-slot="props" field="thickness" label="Thickness">
            <b-numberinput :controls="false" v-model="props.row.thickness"></b-numberinput>
          </b-table-column>
          <b-table-column v-slot="props" field="color" label="Color">
            <b-input v-model="props.row.color"></b-input>
          </b-table-column>
          <b-table-column v-slot="props" field="show_arrow" label="Arrow State">
            <b-select v-model="props.row.show_arrow">
              <option :value="true">Show Arrow</option>
              <option :value="false">Hide Arrow</option>
            </b-select>
          </b-table-column>
          <b-table-column v-slot="props" field="show_step" label="Show Step">
            <b-switch type="is-success" v-model="props.row.show_step"></b-switch>
          </b-table-column>
          <b-table-column v-slot="props" field="action" label="Action">
            <b-button @click="removeArrowFromNumberLine(props.row.id)" type="is-danger" icon-left="close"></b-button>
          </b-table-column>
        </b-table>  
      </div>

      <!--Number settings-->
      <div class="column is-12">
        <p class="title is-5">Number Settings <b-button @click="addNumberRow()" type="is-success" size="is-small">Add</b-button></p>
        
        <b-table v-if="number_line.numbers && number_line.numbers.length" :data="number_line.numbers">
          <b-table-column v-slot="props" field="number" label="Number">
            <b-input v-model="props.row.number"></b-input>
          </b-table-column>
          
          <b-table-column v-slot="props" field="highlight" label="Highlight Number">
            <b-switch type="is-success" v-model="props.row.is_highlight"></b-switch>
          </b-table-column>

          <b-table-column v-slot="props" field="highlight_color" label="Highlight Color">
            <b-input v-model="props.row.highlight_color"></b-input>
          </b-table-column>

          <b-table-column v-slot="props" field="is_select_answer" label="Is Select Answer">
            <b-switch type="is-success" v-model="props.row.is_select_answer"></b-switch>
          </b-table-column>

          <b-table-column v-slot="props" field="action" label="Action">
            <b-button @click="removeNumberRow(props.row.id)" type="is-danger" icon-left="close"></b-button>
          </b-table-column>
        </b-table>  
      </div>
      
      <div class="column is-12">
        <p class="title is-6">View</p>
        <show-number-line :number_line="number_line"></show-number-line>
      </div>
    </div>

  </div>
</template>
<script>
import {mapGetters} from 'vuex'
import {defaultNumberLineValue} from '@/components/Question/NumberLine/utils'
import ShowNumberLine from '@/components/Question/NumberLine/ShowNumberLine'

export default {
  name : 'set-number-line',
  props : {
    value : {
      type : Object
    }, // Here value = new_question.number_line (in parent component)
  }, 
  components : {
    ShowNumberLine,
  },
  data(){
    return {
      number_line : this.value ? this.value : defaultNumberLineValue(),
      inputs : ['']
    }
  },
  computed : {
    ...mapGetters(['']),
  },
  methods : {
    toggle(){
      this.$set(this.number_line, 'show', !this.number_line.show)
    },
    emitData(){
      this.$emit('input', this.number_line)
    },
    addInputToNumberLine(){
      this.number_line.inputs.push('')
    },
    addArrowToNumberLine(){
      this.number_line.arrows.push({
        from : '',
        step : 1,
        show_arrow : false,
        thickness : 1,
        color : '#000',
        show_step : true
      })
    },
    addNumberRow(){
      this.number_line.numbers.push({
        number : null,
        is_highlight : false,
        highlight_color : '#000',
        is_select_answer : false
      })
    },
    removeNumberRow(i){
      this.number_line.numbers.splice(i, 1)
    },
    removeInputFromNumberLine(i){
      this.number_line.inputs.splice(i, 1)
    },
    removeArrowFromNumberLine(i){
      this.number_line.arrows.splice(i, 1)
    }
    
  },
  watch : {
    number_line : {
      handler(){
        this.emitData()
      },
      deep : true
    },
    value : {
      handler(new_data){
        this.number_line = new_data
      },
      deep : true
    }
  }
}
</script>
